/**
 * Client for the live-conversation WebSocket (`GET /sessions/:id/live`, upgraded to the
 * session's Durable Object).
 *
 * Three behaviours matter more than the protocol itself:
 *
 * - **An unknown message is skipped, never fatal.** A protocol addition on the server
 *   must not brick an older client mid-conversation.
 * - **Reconnection is capped and resumes the same session id**, so a lift ride does not
 *   end the conversation and does not start a second one. The Durable Object is keyed
 *   by session id, which is what makes resuming possible at all.
 * - **A dead socket degrades to text rather than spinning.** The learner finishes the
 *   conversation by typing; they are never left holding a dead microphone.
 */

/**
 * Mirrors `ClientMessageSchema` in packages/contracts.
 *
 * The discriminator is `t`, not `type`. An earlier version of this file invented
 * `type`, which type-checked and unit-tested perfectly and was rejected by the real
 * Durable Object on the first live frame — the kind of mismatch only an end-to-end
 * run catches.
 */
export type LiveClientMessage =
  | {
      readonly t: 'hello';
      readonly sessionId: string;
      readonly courseId: string;
      readonly mode: 'voice' | 'text';
      readonly resumeFromTurn?: number;
      readonly clientCaps: {
        readonly opus: boolean;
        readonly sampleRate: 16000 | 24000 | 48000;
        readonly vad: boolean;
      };
    }
  | { readonly t: 'audio'; readonly seq: number; readonly b64: string }
  | { readonly t: 'speech_end'; readonly seq: number }
  | { readonly t: 'text'; readonly value: string }
  | { readonly t: 'barge_in' }
  | { readonly t: 'switch_mode'; readonly mode: 'voice' | 'text' }
  | { readonly t: 'hint' }
  | { readonly t: 'translate' }
  | { readonly t: 'repeat' }
  | { readonly t: 'slow' }
  | { readonly t: 'ping' }
  | { readonly t: 'end'; readonly reason?: 'user' | 'criteria_met' | 'timeout' };

/** Mirrors `ServerMessageSchema`; an unknown `t` is forwarded, never fatal. */
export interface LiveServerMessage {
  readonly t: string;
  readonly [key: string]: unknown;
}

export type LiveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'connecting' }
  | { readonly kind: 'open' }
  | { readonly kind: 'reconnecting'; readonly attempt: number }
  /** Terminal. The caller degrades the session to text. */
  | { readonly kind: 'closed'; readonly reason: string; readonly recoverable: boolean };

export interface LiveSessionOptions {
  readonly baseUrl: string;
  readonly sessionId: string;
  readonly courseId: string;
  readonly accessToken: string;
  readonly mode?: 'voice' | 'text';
  /** Set when reconnecting mid-session; the server replays from its checkpoint. */
  readonly resumeFromTurn?: number;
  readonly onMessage: (message: LiveServerMessage) => void;
  readonly onState: (state: LiveState) => void;
  /** Injected in tests; defaults to the platform WebSocket. */
  readonly socketFactory?: (url: string) => WebSocket;
}

/** Caps reconnection so a genuinely dead connection reports failure, not a spinner. */
const MAX_RECONNECT_ATTEMPTS = 5;
const PING_INTERVAL_MS = 25_000;
/**
 * Two unanswered pings, then reconnect.
 *
 * Fifty seconds of a socket that is open and answering nothing. One missed `pong` can
 * be a slow network; two is a connection that will not come back on its own.
 */
const MAX_UNANSWERED_PINGS = 2;

export class LiveSession {
  private socket: WebSocket | null = null;
  private sequence = 0;
  private attempt = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private closedByCaller = false;
  /** Advanced from server checkpoints so a reconnect resumes rather than restarts. */
  private resumeFromTurn: number | undefined;

  constructor(private readonly options: LiveSessionOptions) {
    this.resumeFromTurn = options.resumeFromTurn;
  }

  get isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  connect(): void {
    this.closedByCaller = false;
    this.options.onState({
      kind: this.attempt === 0 ? 'connecting' : 'reconnecting',
      attempt: this.attempt,
    } as LiveState);

    const url = new URL(
      `/v1/sessions/${this.options.sessionId}/live`,
      this.options.baseUrl.replace(/^http/, 'ws'),
    );
    // A browser WebSocket cannot set an Authorization header, so the token travels as
    // a query parameter over TLS. It is a short-lived access token, never the refresh
    // token, and the server treats it as single-purpose.
    url.searchParams.set('access_token', this.options.accessToken);

    const factory = this.options.socketFactory ?? ((target: string) => new WebSocket(target));
    const socket = factory(url.toString());
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.options.onState({ kind: 'open' });
      this.send({
        t: 'hello',
        sessionId: this.options.sessionId,
        courseId: this.options.courseId,
        mode: this.options.mode ?? 'voice',
        ...(this.resumeFromTurn === undefined ? {} : { resumeFromTurn: this.resumeFromTurn }),
        clientCaps: {
          // MediaRecorder gives us WebM/Opus in every browser we support.
          opus: true,
          sampleRate: 48000,
          // The Recorder does its own VAD, which saves uplink and latency.
          vad: true,
        },
      });
      this.startPings();
    });

    socket.addEventListener('message', (event: MessageEvent) => {
      const parsed = this.parse(event.data);
      if (parsed === null) return;
      // Track the server's checkpoints so a reconnect resumes at the right turn
      // instead of replaying the conversation from the start.
      if (parsed.t === 'checkpoint' && typeof parsed.turn === 'number') {
        this.resumeFromTurn = parsed.turn;
      }
      // Forward compatibility: an unrecognised message is forwarded, not fatal.
      this.options.onMessage(parsed);
    });

    socket.addEventListener('close', () => {
      this.stopPings();
      if (this.closedByCaller) return;
      this.reconnect();
    });

    socket.addEventListener('error', () => {
      // `close` always follows, and reconnecting from both would double the attempts.
      this.stopPings();
    });
  }

  send(message: LiveClientMessage): void {
    if (!this.isOpen) return;
    this.socket?.send(JSON.stringify(message));
  }

  /** Chunks are numbered so the server can detect a gap rather than scoring a hole. */
  sendAudio(base64: string): void {
    this.sequence += 1;
    this.send({ t: 'audio', seq: this.sequence, b64: base64 });
  }

  finishTurn(): void {
    this.send({ t: 'speech_end', seq: this.sequence });
  }

  sendText(value: string): void {
    this.send({ t: 'text', value });
  }

  close(reason: 'user' | 'criteria_met' | 'timeout' = 'user'): void {
    this.closedByCaller = true;
    this.stopPings();
    this.send({ t: 'end', reason });
    this.socket?.close();
    this.socket = null;
    this.options.onState({ kind: 'closed', reason, recoverable: false });
  }

  private parse(data: unknown): LiveServerMessage | null {
    if (typeof data !== 'string') return null;
    try {
      const parsed: unknown = JSON.parse(data);
      if (typeof parsed !== 'object' || parsed === null) return null;
      const message = parsed as LiveServerMessage;
      return typeof message.t === 'string' ? message : null;
    } catch {
      return null;
    }
  }

  private reconnect(): void {
    if (this.attempt >= MAX_RECONNECT_ATTEMPTS) {
      this.options.onState({
        kind: 'closed',
        reason: "We couldn't reconnect. Everything up to now is saved.",
        // Recoverable: the caller offers text mode rather than ending the session.
        recoverable: true,
      });
      return;
    }

    this.attempt += 1;
    this.options.onState({ kind: 'reconnecting', attempt: this.attempt });

    // Exponential backoff with jitter: a server coming back up must not be hit by
    // every disconnected client in the same instant.
    const base = Math.min(8000, 2 ** (this.attempt - 1) * 500);
    const jitter = Math.random() * 300;
    setTimeout(() => this.connect(), base + jitter);
  }

  /**
   * A `pong` came back.
   *
   * The heartbeat used to be write-only: pings went out, nothing read the answers, and
   * a socket that was open but no longer talking to anything looked perfectly healthy.
   */
  notePong(): void {
    this.unansweredPings = 0;
  }

  private startPings(): void {
    this.stopPings();
    this.unansweredPings = 0;
    // Keeps the hibernating Durable Object's idle alarm from closing a session the
    // learner is simply thinking during.
    this.pingTimer = setInterval(() => {
      this.unansweredPings += 1;
      if (this.unansweredPings > MAX_UNANSWERED_PINGS) {
        // Reconnecting is the recovery path the session already has, and it resumes
        // from the Durable Object's checkpoint, so nothing is lost by using it.
        this.socket?.close();
        return;
      }
      this.send({ t: 'ping' });
    }, PING_INTERVAL_MS);
  }

  private unansweredPings = 0;

  private stopPings(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }
}
